<template>
  <div
    ref="pageScroller"
    class="hotel-list-page"
    @scroll.passive="handlePageScroll"
  >
    <header class="page-header">
      <button
        class="back-btn"
        type="button"
        :aria-label="$t('utils.cancel')"
        @click="$router.back()"
      >
        <van-icon name="arrow-left" />
      </button>
      <h1>{{ $t('hotelList.title') }}</h1>
    </header>

    <nav
      v-if="tabs.length"
      class="class-tabs"
    >
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="class-tab"
        :class="{ active: activeClass === tab.id }"
        type="button"
        @click="changeTab(tab.id)"
      >
        <span>{{ tab.title }}</span>
      </button>
    </nav>

    <main class="hotel-content">
      <van-list
        ref="hotelList"
        v-model="listLoading"
        :finished="finished"
        :offset="120"
        :loading-text="$t('hotelList.loading')"
        @load="loadHotels"
      >
        <div
          v-if="hotels.length"
          class="hotel-grid"
        >
          <article
            v-for="item in hotels"
            :key="item.id"
            class="hotel-card"
            role="button"
            tabindex="0"
            @click="goHotelDetail(item)"
            @keyup.enter="goHotelDetail(item)"
          >
            <div class="hotel-image">
              <img
                :src="hotelImage(item)"
                alt=""
                @error="onHotelImageError"
              >
            </div>
            <div class="hotel-title">
              {{ item.title || '' }}
            </div>
            <div class="hotel-meta">
              <span class="hotel-price">${{ formatPrice(item.price) }}</span>
              <span class="hotel-sold">{{ $t('hotelList.sold') }} {{ formatSold(item.sold) }}</span>
            </div>
          </article>
        </div>
        <div
          v-else-if="!requesting"
          class="empty-state"
        >
          {{ $t('hotelList.empty') }}
        </div>
      </van-list>
    </main>
  </div>
</template>

<script>
import Vue from 'vue';
import Fetch from '../../utils/fetch';
import { Icon, List } from 'vant';

Vue.use(Icon).use(List);

export default {
  name: 'Hotellist',
  data() {
    return {
      lang: this.$i18n.locale || 'zh_cn',
      app: {},
      tabs: [],
      activeClass: 0,
      hotels: [],
      listLoading: false,
      requesting: false,
      finished: false,
      page: 1,
      limit: 20,
      hotelFallback: require('../images/shop2/pro.png'),
    };
  },
  created() {
    if (this.$parent && this.$parent.footer) {
      this.$parent.footer('hotellist');
    }
  },
  mounted() {
    this.start();
  },
  methods: {
    start() {
      Fetch('/index/webconfig').then((r) => {
        this.app = r.data || {};
      });
      this.loadHotels();
    },
    loadHotels() {
      if (this.requesting || this.finished) {
        this.listLoading = false;
        return;
      }
      this.requesting = true;
      this.listLoading = true;
      Fetch('/index/pros_search', {
        class: this.activeClass,
        page: this.page,
        limit: this.limit,
        language: this.lang,
      }).then((r) => {
        var data = r.data || {};
        var list = Array.isArray(data.list) ? data.list : [];
        this.tabs = Array.isArray(data.cates) ? data.cates : [];
        this.activeClass = Number(data.active_class || 0);
        this.hotels = this.page === 1 ? list : this.hotels.concat(list);
        this.finished = !data.has_more || list.length < this.limit;
        this.page += 1;
      }).catch(() => {
        if (this.page === 1) {
          this.hotels = [];
        }
        this.finished = true;
      }).finally(() => {
        this.requesting = false;
        this.listLoading = false;
        this.checkList();
      });
    },
    changeTab(id) {
      var nextId = Number(id || 0);
      if (this.activeClass === nextId) {
        return;
      }
      this.activeClass = nextId;
      this.resetList();
      this.loadHotels();
    },
    resetList() {
      this.page = 1;
      this.hotels = [];
      this.finished = false;
      this.listLoading = false;
      this.requesting = false;
    },
    goHotelDetail(item) {
      if (!item || !item.id) {
        return;
      }
      this.$router.push({
        path: '/hotelit',
        query: {
          id: item.id,
        },
      });
    },
    handlePageScroll(event) {
      var target = event && event.target ? event.target : this.$refs.pageScroller;
      if (!target || this.requesting || this.finished) {
        return;
      }
      var distance = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distance <= 120) {
        this.loadHotels();
      }
    },
    checkList() {
      this.$nextTick(() => {
        if (this.$refs.hotelList && typeof this.$refs.hotelList.check === 'function') {
          this.$refs.hotelList.check();
        }
        this.handlePageScroll({
          target: this.$refs.pageScroller,
        });
      });
    },
    kefuTo() {
      if (this.app && this.app.kefu_link) {
        window.location.href = this.app.kefu_link;
      }
    },
    buildImageUrl(path, fallback) {
      if (!path) {
        return fallback;
      }
      if (/^(https?:)?\/\//.test(path) || String(path).indexOf('data:') === 0) {
        return path;
      }
      if (this.app && this.app.domain) {
        return this.app.domain.replace(/\/$/, '') + '/' + String(path).replace(/^\//, '');
      }
      return path;
    },
    hotelImage(item) {
      return this.buildImageUrl(item && item.img, this.hotelFallback);
    },
    onHotelImageError(event) {
      if (event && event.target && event.target.src !== this.hotelFallback) {
        event.target.src = this.hotelFallback;
      }
    },
    formatPrice(value) {
      var number = Number(value);
      if (!isFinite(number)) {
        number = 0;
      }
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatSold(value) {
      var number = Number(value);
      if (!isFinite(number) || number < 0) {
        number = 0;
      }
      if (number >= 1000) {
        var shortNumber = number / 1000;
        var fixed = shortNumber >= 10 ? shortNumber.toFixed(0) : shortNumber.toFixed(1);
        return fixed.replace(/\.0$/, '') + 'k+';
      }
      return String(Math.floor(number));
    },
  },
};
</script>

<style lang="less" scoped>
.hotel-list-page {
  height: 100vh;
  min-height: 100vh;
  padding-bottom: 72px;
  overflow-y: auto;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
  background: #f3f4fa;
  color: #06152b;
}

.page-header {
  position: sticky;
  top: 0;
  z-index: 8;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4fa;
}

.page-header h1 {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: #06152b;
}

.back-btn {
  position: absolute;
  left: 16px;
  top: 50%;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  transform: translateY(-50%);
  background: transparent;
  color: #06152b;
  font-size: 14px;
}

.class-tabs {
  position: sticky;
  top: 50px;
  z-index: 7;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #edf0f6;
}

.class-tab {
  position: relative;
  min-width: 0;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: #344055;
  font-size: 14px;
  line-height: 60px;
  text-align: center;
}

.class-tab span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.class-tab.active {
  color: #06152b;
  font-weight: 700;
}

.class-tab.active::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 54px;
  height: 3px;
  border-radius: 3px;
  transform: translateX(-50%);
  background: #2389ff;
}

.hotel-content {
  padding: 16px 16px 4px;
}

.hotel-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 18px;
  row-gap: 18px;
}

.hotel-card {
  min-width: 0;
  cursor: pointer;
  outline: none;
}

.hotel-image {
  width: 100%;
  height: 102px;
  overflow: hidden;
  border-radius: 6px;
  background: #e5e7ef;
}

.hotel-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hotel-title {
  height: 34px;
  margin-top: 6px;
  overflow: hidden;
  color: #06152b;
  font-size: 14px;
  line-height: 17px;
  word-break: break-word;
}

.hotel-meta {
  display: flex;
  align-items: baseline;
  min-width: 0;
  margin-top: 2px;
  line-height: 16px;
}

.hotel-price {
  margin-right: 4px;
  color: #ff0b0b;
  font-size: 14px;
  font-weight: 700;
}

.hotel-sold {
  min-width: 0;
  color: #9aa0ad;
  font-size: 12px;
  white-space: nowrap;
}

.empty-state {
  padding: 80px 20px;
  color: #9aa0ad;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.kefu {
  position: fixed;
  right: 15px;
  bottom: 88px;
  z-index: 80;
  width: 62px;
  height: 62px;
}

.kefu_img {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  border: 4px solid #fff;
  border-radius: 50%;
  background: linear-gradient(180deg, #1b7dff 0%, #0d80ff 64%, #fb1426 65%, #fb1426 100%);
  box-shadow: 0 6px 14px rgba(30, 70, 130, 0.25);
  color: #fff;
}

.kefu_img .van-icon {
  font-size: 31px;
}
</style>
